import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Link2, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ThinkingAnimation from "./ThinkingAnimation";
import EmailCaptureModal from "./EmailCaptureModal";

interface LeadData {
  productUrl: string;
  campaignDescription: string;
}

const quickActions = [
  { label: "Ad Creatives", value: "Generate ad creatives for my product" },
  { label: "Product Copy", value: "Write compelling product descriptions" },
  { label: "Optimized Page", value: "Optimize my product page for conversions" },
];

const placeholderTexts = [
  "Describe your product, campaign, or target audience...",
  "Tell us about your brand's unique selling points...",
  "What goals do you want to achieve with this campaign?",
  "Share your product story and key benefits...",
  "Describe your ideal customer and their needs...",
];

const LeadMagnetInput = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [productUrl, setProductUrl] = useState("");
  const [campaignDescription, setCampaignDescription] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  
  // Typewriter effect state
  const [placeholder, setPlaceholder] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Typewriter effect
  useEffect(() => {
    const currentText = placeholderTexts[textIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (charIndex < currentText.length) {
          setPlaceholder(currentText.substring(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          // Pause before deleting
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        // Deleting
        if (charIndex > 0) {
          setPlaceholder(currentText.substring(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } else {
          setIsDeleting(false);
          setTextIndex((textIndex + 1) % placeholderTexts.length);
        }
      }
    }, isDeleting ? 30 : 50);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, textIndex]);

  // Flexible URL validation - accepts with/without http(s) and www
  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    // Pattern to match domains like example.com, www.example.com, http(s)://example.com
    const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z]{2,})+(\/.*)?$/;
    return urlPattern.test(url.trim());
  };

  const handleGenerate = async () => {
    if (!isValidUrl(productUrl)) {
      toast({
        title: "Product URL required",
        description: "Please enter a valid product URL (e.g., example.com or www.example.com)",
        variant: "destructive",
      });
      return;
    }

    setLeadData({
      productUrl,
      campaignDescription,
    });

    setIsThinking(true);

    setTimeout(() => {
      setIsThinking(false);
      setShowEmailModal(true);
    }, 2500);
  };

  const handleEmailSubmit = async (email: string) => {
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("capture-lead", {
        body: {
          email,
          productUrl: leadData?.productUrl || "",
          campaignDescription: leadData?.campaignDescription || "",
        },
      });

      if (error) {
        throw error;
      }

      localStorage.setItem(
        "leadMagnetData",
        JSON.stringify({
          email,
          ...leadData,
        })
      );

      setShowEmailModal(false);
      navigate("/generate");
    } catch (err) {
      console.error("Error capturing lead:", err);
      toast({
        title: "Something went wrong",
        description: "Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleQuickAction = (value: string) => {
    setCampaignDescription(value);
  };

  if (isThinking) {
    return (
      <div className="w-full max-w-2xl mx-auto animate-fade-in">
        <ThinkingAnimation />
      </div>
    );
  }

  return (
    <>
      <div className="w-full max-w-2xl mx-auto animate-fade-in">
        {/* Main Input Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl transition-shadow duration-300">
          {/* Spacious Textarea */}
          <Textarea
            placeholder={placeholder}
            value={campaignDescription}
            onChange={(e) => setCampaignDescription(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[100px] sm:min-h-[120px] border-0 resize-none focus-visible:ring-0 focus:ring-2 focus:ring-blue-100 focus:bg-blue-50/30 rounded-lg text-base sm:text-lg placeholder:text-gray-400 p-2 -m-2 transition-all duration-200"
          />

          {/* Bottom Toolbar - stacked on mobile, row on desktop */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center sm:justify-between pt-4 mt-4 border-t border-gray-100">
            {/* URL Input - full width on mobile */}
            <div className="flex items-center gap-2 text-gray-500 w-full sm:w-auto sm:flex-1 sm:max-w-sm bg-gray-100 rounded-lg px-3 py-2">
              <Link2 size={18} className="flex-shrink-0" />
              <input
                type="url"
                placeholder="Product URL"
                value={productUrl}
                onChange={(e) => setProductUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                className="text-sm bg-transparent border-0 focus:outline-none flex-1 placeholder:text-gray-400 text-gray-600"
              />
            </div>

            {/* Generate Button - full width on mobile */}
            <Button onClick={handleGenerate} className="w-full sm:w-auto h-11 gap-2">
              Generate
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>

        {/* Quick Action Pills - horizontal scroll on mobile, wrap on desktop */}
        <div className="flex items-center gap-3 mt-6 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap sm:justify-center sm:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <span className="text-sm text-muted-foreground flex-shrink-0">Try:</span>
          {quickActions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => handleQuickAction(action.value)}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap flex-shrink-0"
            >
              {action.label}
            </button>
          ))}
        </div>

        {/* Shopify Import - subtle */}
        <div className="flex justify-center mt-4">
          <button
            type="button"
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-600 transition-colors py-2"
          >
            <ShoppingBag size={14} />
            <span>Import from Shopify</span>
          </button>
        </div>
      </div>

      <EmailCaptureModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        onSubmit={handleEmailSubmit}
        isLoading={isSubmitting}
      />
    </>
  );
};

export default LeadMagnetInput;
